import { exportToPdf } from '~/components/PriceParrot/PriceParrotParityTool/exportPdf';

import { buildCommercialApprovalHtml } from './buildCommercialApprovalHtml';
import type { CommercialApprovalData } from './types';

/**
 * Renders the same document `exportCommercialApprovalHtml` downloads into a hidden,
 * off-screen container and captures it with the repo's existing jsPDF + html2canvas
 * convention, section by section (each `[data-pdf-section]` card is its own screenshot).
 * The PDF is a render of the exact same markup as the HTML export, so the two never drift.
 */
export async function exportCommercialApprovalPdf(data: CommercialApprovalData): Promise<void> {
  const html = buildCommercialApprovalHtml(data);
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const styleTag = parsed.querySelector('style')?.outerHTML ?? '';

  // exportToPdf resolves its section root via `reportElement.firstElementChild`, so the
  // sections must live inside a single wrapper div - not as siblings of `container` itself,
  // or that lookup lands on the <style> tag instead and silently falls back to screenshotting
  // the whole document as one image with no section-aware page breaks.
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;width:1000px;';
  const contentRoot = document.createElement('div');
  contentRoot.innerHTML = styleTag + parsed.body.innerHTML;
  container.appendChild(contentRoot);
  document.body.appendChild(container);

  // The Roboto <link> from buildCommercialApprovalHtml's <head> isn't carried over above (only
  // body content + inline <style> are). Without it, html2canvas captures with the fallback font
  // stack instead - each fallback has different character widths, which can push legend/label
  // text past a wrap threshold that never triggers with Roboto loaded. Load it into the real
  // document <head> so it actually downloads, and wait for it before capturing.
  const fontLinks = Array.from(
    parsed.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"], link[rel="preconnect"]'),
  ).map((link) => link.cloneNode(true) as HTMLLinkElement);
  fontLinks.forEach((link) => document.head.appendChild(link));

  try {
    await document.fonts.ready;

    const dateSlug = new Date()
      .toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })
      .replace(/\//g, '-');
    const filename = `Commercial Approval - ${data.propertyName.replace(/[^a-zA-Z0-9_\- ]/g, '')} - ${dateSlug}.pdf`;
    await exportToPdf(container, filename);
  } finally {
    document.body.removeChild(container);
    fontLinks.forEach((link) => link.remove());
  }
}
